import '@adornis/file-utils/client/x-image.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { currentUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import { stopImpersonation } from '@adornis/users/client/impersonation.js';
import { html, nothing } from 'lit';
import packagejson from '../../../package.json' with { type: 'json' };
import * as m from '../../../localization/out/messages.js';
import type { Company } from '../../db/company.js';
import { mainRouter } from '../mainRouter.js';
import { UserToolbar } from './user-toolbar.js';

export const AdminCompanyLayout = stateful(
  ({
    sidebarContent,
    pageTitle,
    content,
    company,
  }: {
    sidebarContent: Renderable;
    pageTitle: Renderable;
    content: Renderable;
    company?: Company | null;
  }) => {
    const user = useObservable(() => currentUser(), []);
    const isImpers = useObservable(() => isImpersonating, []);

    return html`
      <div class="drawer lg:drawer-open h-screen">
        <input id="main-drawer" type="checkbox" class="drawer-toggle" />
        <div class="drawer-content flex flex-col overflow-y-auto bg-base-100">
          <div class="w-full navbar sticky top-0 z-10 backdrop-blur bg-base-100">
            <div class="flex-none lg:hidden">
              <label
                for="main-drawer"
                aria-label="${m.admin_company_layout_open_sidebar()}"
                class="btn btn-square btn-ghost"
              >
                <span class="icon-[material-symbols--menu] inline-block w-6 h-6"></span>
              </label>
            </div>
            ${pageTitle} ${UserToolbar()}
          </div>
          <div class="p-4 sm:p-6 md:p-8 bg-base-200 flex-1">
            <div class="max-w-7xl mx-auto">${content}</div>
          </div>
        </div>
        <div class="drawer-side z-1000">
          <label
            for="main-drawer"
            aria-label="${m.admin_company_layout_close_sidebar()}"
            class="drawer-overlay"
          ></label>
          <ul class="menu p-4 w-80 min-h-full bg-base-100/90 backdrop-blur">
            <div class="px-4 py-3 mb-2 flex items-center gap-3 w-full">
              ${
                company?.logoID
                  ? html`
                      <div class="avatar">
                        <div class="rounded-full w-10 h-10 overflow-hidden">
                          <x-image
                            class="h-full"
                            .value=${company.logoID}
                            mode="cover"
                            alt="${company.name || m.company_profile_logo_alt()}"
                          ></x-image>
                        </div>
                      </div>
                    `
                  : nothing
              }
              <div class="flex flex-col gap-1 flex-1 min-w-0">
                <div class="font-bold text-lg">Matching Week</div>
                ${user ? user.email : nothing}
              </div>
            </div>
            ${sidebarContent}
            <div class="absolute bottom-4 left-4 flex flex-row justify-between w-[calc(100%-8*var(--spacing))]">
              <span class="badge badge-ghost">v.${packagejson.version}</span>
              ${
                isImpers
                  ? html`
                      <button
                        class="btn btn-ghost btn-xs"
                        title="${m.admin_company_layout_stop_impersonation()}"
                        @click=${() => {
                          stopImpersonation();
                          mainRouter.goTo('root');
                          setTimeout(() => window.location.reload(), 100);
                        }}
                      >
                        <span class="icon-[material-symbols--person-off]"></span>
                        <span class="hidden sm:inline">${m.admin_company_layout_stop_impersonation()}</span>
                      </button>
                    `
                  : nothing
              }
            </div>
          </ul>
        </div>
      </div>
    `;
  },
);
